// src/hooks/useAnalytics.ts
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

// Custom hook for analytics tracking
export const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views when route changes
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', 'G-952EXK9NGR', {
        page_path: location.pathname + location.search + location.hash,
        page_title: document.title
      });
    }
  }, [location]);
};

// Track custom events
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      event_category: 'engagement',
      event_label: window.location.pathname,
      ...parameters
    });
  }
};

// Track scroll depth
export const useScrollDepthTracking = (thresholds: number[] = [25, 50, 75, 90]) => {
  useEffect(() => {
    const trackedThresholds = new Set<number>();
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);
      
      thresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !trackedThresholds.has(threshold)) {
          trackedThresholds.add(threshold);
          trackEvent('scroll_depth', {
            event_category: 'engagement',
            event_label: `${threshold}%`,
            value: threshold,
            page_path: window.location.pathname
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [thresholds]);
};

// Track time spent on page
export const useTimeOnPageTracking = () => {
  useEffect(() => {
    const startTime = Date.now();
    const intervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
    const tracked = new Set<number>();

    const trackTimeSpent = () => {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      
      intervals.forEach(interval => {
        if (timeSpent >= interval && !tracked.has(interval)) {
          tracked.add(interval);
          trackEvent('time_on_page', {
            event_category: 'engagement',
            event_label: `${interval}s`,
            value: interval,
            page_path: window.location.pathname
          });
        }
      });
    };

    const timer = setInterval(trackTimeSpent, 10000); // Check every 10 seconds
    
    return () => clearInterval(timer);
  }, []);
};

// Track section visibility (when sections come into view)
export const useSectionTracking = (sectionId: string, sectionName: string) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEvent('section_view', {
              event_category: 'content_engagement',
              event_label: sectionName,
              section_id: sectionId,
              page_path: window.location.pathname
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of section is visible
    );

    const element = document.getElementById(sectionId);
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [sectionId, sectionName]);
};
